import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';

import { isDateLegalAge, stringToIsoDate } from '@utils/date';
import { ignore } from '@utils/object';
import { TOAST_STATE } from '@shared/constants';
import AgeValidationModalContainer from './components/AgeValidationModalContainer';
import {
  AGE_VALIDATION_TYPE,
  AGE_VALIDATION_MODAL_ACTION,
  AGE_VALIDATION_MODAL_TYPE,
  AGE_VALIDATION_MODAL_CLICK,
} from './constants';

const { COMPLETED_UNDERAGE } = AGE_VALIDATION_MODAL_ACTION;
const { DATE_OF_BIRTH, IS_OF_AGE } = AGE_VALIDATION_TYPE;
const { BUTTON, HIGHER } = AGE_VALIDATION_MODAL_CLICK;

const PROPS = {
  hasToValidateAge: PropTypes.bool.isRequired,
  ageRestriction: PropTypes.object.isRequired,

  showToast: PropTypes.func.isRequired,
  saveAgeValidation: PropTypes.func.isRequired,
  trackModalOpened: PropTypes.func.isRequired,
  trackModalClosed: PropTypes.func.isRequired,

  intl: intlShape.isRequired,
};

export default function WithAgeValidation(
  T,
  { errorMsg = '', subtitle = '', infoMsg = '' } = {},
) {
  return class WithAgeValidationFn extends React.PureComponent {
    static propTypes = PROPS;

    state = {
      showModal: false,
    };

    validateDateOfBirth(date) {
      const {
        ageRestriction: { ageOfMajority },
      } = this.props;

      return date && isDateLegalAge(date, ageOfMajority);
    }

    onCloseAgeValidation = (action, clickLocation) => {
      const {
        trackModalClosed,
        showToast,
        intl: { formatMessage },
      } = this.props;
      this.setState({ showModal: false });
      trackModalClosed(AGE_VALIDATION_MODAL_TYPE, action, clickLocation);
      if (infoMsg !== '') {
        showToast({
          title: formatMessage(infoMsg),
          color: TOAST_STATE.ERROR,
        });
      }
      if (this.onClose) this.onClose();
    };

    onAcceptAgeValidation = async birthDate => {
      const {
        intl: { formatMessage },
        showToast,
        saveAgeValidation,
        trackModalClosed,
        ageRestriction: { ageValidationType },
      } = this.props;

      try {
        let isValid = false;
        let date;
        let modalAction = COMPLETED_UNDERAGE;

        if (ageValidationType === DATE_OF_BIRTH && birthDate) {
          date = stringToIsoDate(birthDate);
          isValid = this.validateDateOfBirth(date);
        } else if (ageValidationType === IS_OF_AGE) {
          isValid = true;
        }

        this.setState({ showModal: false });

        if (isValid) {
          await saveAgeValidation({
            date: date || true,
            type: ageValidationType,
          });
          modalAction = HIGHER;
        } else {
          showToast({
            title: formatMessage(errorMsg),
            color: TOAST_STATE.ERROR,
          });
        }
        trackModalClosed(AGE_VALIDATION_MODAL_TYPE, modalAction, BUTTON);
        if (this.onValidate) this.onValidate(isValid);
      } catch (e) {
        showToast({
          title: formatMessage(errorMsg),
          color: TOAST_STATE.ERROR,
        });
      }
    };

    openAgeValidation = ({ onValidate, onClose } = {}) => {
      const { hasToValidateAge } = this.props;

      if (hasToValidateAge) {
        const { trackModalOpened } = this.props;
        this.setState({ showModal: true });
        trackModalOpened(AGE_VALIDATION_MODAL_TYPE);
        this.onValidate = onValidate;
        this.onClose = onClose;
      }
    };

    getPropsToInject() {
      return ignore(this.props, Object.keys(PROPS));
    }

    render() {
      const {
        intl: { formatMessage },
        hasToValidateAge,
        ageRestriction: { ageValidationType, ageOfMajority },
      } = this.props;

      const { showModal } = this.state;

      return (
        <>
          <T
            hasToValidateAge={hasToValidateAge}
            openAgeValidation={this.openAgeValidation}
            {...this.getPropsToInject()}
          />

          <AgeValidationModalContainer
            open={showModal}
            onAccept={this.onAcceptAgeValidation}
            onClose={this.onCloseAgeValidation}
            type={ageValidationType}
            ageOfMajority={ageOfMajority}
            message={formatMessage(subtitle)}
          />
        </>
      );
    }
  };
}
